import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PawPrint, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/app/Logo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-background">
      <Card className="w-full max-w-md border-border shadow-none rounded-lg" data-testid="reset-password-card">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-8">
            <Logo size={64} textClassName="text-2xl" />
          </div>

          {tokenMissing ? (
            <div data-testid="reset-missing-token">
              <h1 className="font-display text-2xl font-extrabold tracking-tight mb-3">Missing reset token</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Open this page from the link in the email we sent you. If it expired, request a new one.
              </p>
              <Link to="/forgot-password" className="text-sm font-medium underline underline-offset-4">Request a new link</Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <Card className="border-border shadow-none rounded-lg bg-secondary mb-6">
                <CardContent className="p-5 flex items-start gap-3">
                  <ShieldCheck size={20} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Password updated</p>
                    <p className="text-sm text-muted-foreground mt-0.5">Taking you to sign in…</p>
                  </div>
                </CardContent>
              </Card>
              <Link to="/login" className="text-sm font-medium underline underline-offset-4">Go to sign in</Link>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Choose a new password</p>
              <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-8">
                Set a new password
              </h1>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="pw1">New password</Label>
                  <Input
                    id="pw1"
                    data-testid="reset-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="pw2">Confirm new password</Label>
                  <Input
                    id="pw2"
                    data-testid="reset-confirm-input"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5"
                    required
                    minLength={6}
                  />
                </div>
                {error ? (
                  <div data-testid="reset-error" className="text-sm text-destructive">{error}</div>
                ) : null}
                <Button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
